import { rem } from 'polished'
import React, { Fragment, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import Divider from 'LuxKit/Divider'
import MarkdownRender from 'LuxKit/MarkdownRender'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import PriceSummaryRowItemDescription from 'LuxKit/PriceSummaries/PriceSummaryRowItemDescription'
import PriceSummaryRowItemDescriptionDiscount from 'LuxKit/PriceSummaries/PriceSummaryRowItemDescriptionDiscount'
import PriceSummaryRowItemSubtitle from 'LuxKit/PriceSummaries/PriceSummaryRowItemSubtitle'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import useAgentHubCommission from 'agentHub/hooks/useAgentHubCommissionV2'
import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Expander from 'components/utils/Expander'
import FormatPercent from 'components/utils/Formatters/FormatPercent'
import Plural from 'components/utils/Formatters/Plural'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'
import { useAppSelector } from 'hooks/reduxHooks'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useToggle from 'hooks/useToggle'
import { EmptyArray, sum } from 'lib/array/arrayUtils'
import offerPageURL, { getHotelOfferUrl } from 'lib/offer/offerPageURL'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { FormattedMessage, useIntl } from 'react-intl'

import TaxAndFeesText from './TaxAndFeesText'

const PriceDetailsModalExtraDesc = styled.div`
  width: 100%;
  background-color: ${props => props.theme.palette.brand.primary.lightest};
  padding: ${rem(12)};
  margin-top: ${rem(24)};
`

const PaymentSchedule = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.default.seven};
  padding: ${rem(16)};
`

export type PriceDetailsModalOfferProps = {
  offer: App.Offer | App.BedbankOffer | App.OfferSummary | App.BedbankOfferSummary
  offerId?: undefined
  offerType?: undefined
  saleUnit?: undefined
  displayPricingAsPerPerson?: undefined
} | {
  offer?: undefined
  offerId: string
  offerType: App.AlwaysOnOfferType | App.BedbankOfferType
  saleUnit: string
  displayPricingAsPerPerson: boolean
}

interface ResolvedOfferProps {
  offerId: string
  isBedbank: boolean
  saleUnit: string
  shouldDisplayPricingAsPerPerson: boolean
  finePrintUrl: string
}

function resolveOfferProps({
  offer,
  offerId,
  offerType,
  saleUnit,
  displayPricingAsPerPerson,
}: PriceDetailsModalOfferProps): ResolvedOfferProps {
  if (offer) {
    const isBedbank = isBedbankOffer(offer)
    return {
      offerId: offer.id,
      isBedbank,
      saleUnit: offer.saleUnit,
      shouldDisplayPricingAsPerPerson: isBedbank && offer.displayPricingAsPerPerson,
      finePrintUrl: offerPageURL(offer, undefined, '#fine-print'),
    }
  }
  const isBedbank = offerType === OFFER_TYPE_BED_BANK
  return {
    offerId,
    isBedbank,
    saleUnit,
    shouldDisplayPricingAsPerPerson: isBedbank && displayPricingAsPerPerson,
    // This link will be missing the slug, but the above link is missing the region code, so a
    // redirect is inevitable.
    finePrintUrl: `${getHotelOfferUrl(offerId, offerType)}#fine-print`,
  }
}

type Props = {
  offerPage?: boolean
  duration: number
  taxesAndFees?: number
  propertyFees?: number
  /** The price **without property fees** */
  hotelPrice: number
  /** The member price **without property fees** */
  hotelMemberPrice?: number
  showMemberPrice?: boolean
  flightPrice?: number
  otherFees?: {
    propertyFees: number
    extraGuestsSurcharge: number
  }
  rooms?: Array<App.Occupants>
  extraGuestMessage?: string
  dueAtPropertyMessage?: string
  propertiesFees?: Array<App.PropertyFeesDetails>
  cta?: React.ReactNode
  onCtaAfterClose?: () => void
  hasMobilePromotion?: boolean
  rateId?: string
  discount?: number
} & PriceDetailsModalOfferProps

function PriceDetailsModal({
  offerPage,
  duration,
  taxesAndFees = 0,
  propertyFees = 0,
  propertiesFees = EmptyArray,
  hotelPrice,
  flightPrice = 0,
  rooms,
  extraGuestMessage,
  dueAtPropertyMessage,
  cta,
  hotelMemberPrice = 0,
  showMemberPrice,
  otherFees,
  hasMobilePromotion,
  rateId,
  discount,
  ...offerProps
}: Props) {
  const {
    offerId,
    isBedbank,
    saleUnit,
    shouldDisplayPricingAsPerPerson,
    finePrintUrl,
  } = resolveOfferProps(offerProps)

  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const enabledShowMemberPrice = showMemberPrice && luxPlusEnabled

  const [expanded, expand] = useToggle(false)

  const { resolve } = useModalElementContext<'fine-print' | undefined>()

  const dueAtProperty = (otherFees?.propertyFees || 0) + (otherFees?.extraGuestsSurcharge || 0)
  const priceBeforeTax = hotelPrice - taxesAndFees - dueAtProperty + flightPrice
  const priceAfterTax = hotelPrice + flightPrice
  const memberPriceAfterTax = hotelMemberPrice + flightPrice

  const adultsNumber = useMemo(() => sum(rooms ?? [], room => room.adults) || 2, [rooms])

  const { commissionPercentage, packagesCommission } = useAgentHubCommission(offerId)

  const customerView = useAppSelector(state => state.agentHub.customerView)
  const showAgentView = config.agentHub.isEnabled && !customerView && !!commissionPercentage

  const total = priceAfterTax + propertyFees - (discount ?? 0)
  const memberTotal = memberPriceAfterTax + propertyFees - (discount ?? 0)
  const commissionTotal = total * (commissionPercentage / 100)

  const onFinePrint = useCallback(() => resolve('fine-print'), [resolve])

  const intl = useIntl()
  const commissionToDisplay = config.agentHub.isEnabled && isBedbank ? packagesCommission.find(pkg => pkg.packageId === rateId)?.commissionPercentage ?? commissionPercentage : commissionPercentage

  const totalLabel = enabledShowMemberPrice ? (
    <FormattedMessage
      id="zoN1JUjHK/"
      defaultMessage="Total for non-members"
      description="Label for total price for non-members when member pricing is shown"
    />
  ) : (
    <FormattedMessage
      id="zdgh7vzF25"
      defaultMessage="Total"
      description="Label for total price"
    />
  )

  const toggleButtonText = expanded
    ? intl.formatMessage({
        id: 'ESBTGMa07e',
        defaultMessage: 'Read Less',
        description: 'Button text to collapse expanded content',
      })
    : intl.formatMessage({
        id: 'HN/Le6EGYP',
        defaultMessage: 'Learn More',
        description: 'Button text to expand content',
      })

  return <ModalBase size="L">
    <ModalHeader
      title={intl.formatMessage({
        id: 'ZkPLoarfe0',
        defaultMessage: 'Price details',
        description: 'Title for price details modal',
      })}
    />
    <ModalBody>
      <ModalContent>
        <Group direction="vertical" gap={12}>
          <div>
            <PriceSummaryRowItemSubtitle
              label={<>
                <Plural singular="night" count={duration} withCount />
                {' '}
                {shouldDisplayPricingAsPerPerson && <Plural singular="person" count={adultsNumber} withCount />}
                {!shouldDisplayPricingAsPerPerson && <Plural singular={saleUnit} count={rooms?.length || 1} withCount />}
              </>}
              value={priceBeforeTax}
              valueFormat="roundedDollar"
            />
            {duration > 1 && <PriceSummaryRowItemDescription
              label={intl.formatMessage({
                id: '81hZ6b/mry',
                defaultMessage: 'Average per night',
                description: 'Label for average price per night',
              })}
              value={priceBeforeTax / duration}
              valueFormat="roundedDollar"
            />}
          </div>
          {discount && <>
            <Divider direction="horizontal" kind="secondary" />
            <PriceSummaryRowItemDescriptionDiscount
              label={intl.formatMessage({
                id: 'HbuKYv97HP',
                defaultMessage: 'Promo Code',
                description: 'Label for promo code discount',
              })}
              discountValue={discount}
              discountValueFormat="roundedDollar"
            />
          </>}
          {hasMobilePromotion && <>
            <Divider direction="horizontal" kind="secondary" />
            <VerticalSpacer gap={4}>
              <PriceSummaryRowItemDescriptionDiscount
                label={intl.formatMessage({
                  id: '1qLSXgk2BE',
                  defaultMessage: 'Mobile only price',
                  description: 'Label for mobile-only promotional price',
                })}
              />
              <PriceSummaryRowItemDescription
                label={intl.formatMessage({
                  id: '44i0PXfKkQ',
                  defaultMessage: "You're getting a discounted price only available on mobile and tablet devices",
                  description: 'Description for mobile-only promotional price',
                })}
              />
            </VerticalSpacer>
          </>}
          {taxesAndFees > 0 && <>
            <Divider direction="horizontal" kind="secondary" />
            <VerticalSpacer gap={4}>
              <PriceSummaryRowItemDescription
                label={intl.formatMessage({
                  id: 'ZXywJdjhUK',
                  defaultMessage: 'Taxes and fees',
                  description: 'Label for taxes and fees',
                })}
                value={taxesAndFees}
                valueFormat="roundedDollar"
              />
              <Expander expanded={expanded}>
                <TaxAndFeesText
                  isBedbank={isBedbank}
                  offerPage={!!offerPage}
                  finePrintUrl={finePrintUrl}
                  onFinePrint={onFinePrint}
                />
              </Expander>
              <SmallToggleButton onClick={expand} on={expanded} size="small">
                {toggleButtonText}
              </SmallToggleButton>
            </VerticalSpacer>
          </>}
          {!otherFees && propertyFees > 0 && propertiesFees.length === 0 && <>
            <Divider direction="horizontal" kind="secondary" />
            <PriceSummaryRowItemSubtitle
              label={intl.formatMessage({
                id: 'k6I1K8c9Er',
                defaultMessage: 'Due at property',
                description: 'Label for fees due at property',
              })}
              value={propertyFees}
              valueFormat="roundedDollar"
            />
          </>}
          {!otherFees && propertiesFees.length > 0 && <>
            <Divider direction="horizontal" kind="secondary" />
            <div>
              {propertiesFees.map((fee) => <Fragment key={fee.propertyName}>
                <PriceSummaryRowItemSubtitle
                  label={intl.formatMessage({
                    id: 'M9zgxY4Xo5',
                    defaultMessage: 'Local tax',
                    description: 'Label for local tax',
                  })}
                  value={fee.propertyFees}
                  valueFormat="roundedDollar"
                />
                <PriceSummaryRowItemDescription label={intl.formatMessage(
                  {
                    id: 'ZZq8VzYTnJ',
                    defaultMessage: '(Payable at {propertyName})',
                    description: 'Label indicating where local tax is payable',
                  },
                  { propertyName: fee.propertyName },
                )}
                />
              </Fragment>)}
            </div>
          </>}
          <Divider direction="horizontal" kind="secondary" />
          {showAgentView && <>
            <PriceSummaryRowItemSubtitle
              label={
                <FormattedMessage
                  id="8M/Xacas/Q"
                  defaultMessage="Total commission (<percentage></percentage>)"
                  description="Label for total commission with percentage"
                  values={{
                    percentage: () => <FormatPercent key="percent" value={commissionToDisplay / 100} />,
                  }}
                />
              }
              value={commissionTotal * -1}
              valueFormat="roundedDollar"
            />
            <PriceSummaryRowItemSubtitle
              label={intl.formatMessage({
                id: '1nl8M3Cjog',
                defaultMessage: 'Gross total',
                description: 'Label for gross total',
              })}
              value={total}
              valueFormat="roundedDollar"
            />
            <PriceSummaryRowItemSubtitle
              label={intl.formatMessage({
                id: 'ishwArVCht',
                defaultMessage: 'Net total',
                description: 'Label for net total',
              })}
              value={total - commissionTotal}
              valueFormat="roundedDollar"
            />
          </>}
          {!showAgentView && <>
            <PriceSummaryRowItemSubtitle
              label={totalLabel}
              value={total}
              valueFormat="roundedDollar"
            />
            {enabledShowMemberPrice && <>
              <Divider direction="horizontal" kind="secondary" />
              <PriceSummaryRowItemDescriptionDiscount
                label={intl.formatMessage(
                  {
                    id: 'KppC9pJrai',
                    defaultMessage: '{programName} member discount',
                    description: 'Label for member discount',
                  },
                  { programName: LUXURY_PLUS.PROGRAM_NAME },
                )}
                discountValue={memberPriceAfterTax - priceAfterTax}
                discountValueFormat="roundedDollar"
              />
              <Divider direction="horizontal" kind="secondary" />
              <PriceSummaryRowItemSubtitle
                label={intl.formatMessage(
                  {
                    id: 'CZMA9SMNDC',
                    defaultMessage: 'Total for {programName} members',
                    description: 'Label for total price for members',
                  },
                  { programName: LUXURY_PLUS.PROGRAM_NAME },
                )}
                value={memberTotal}
                valueFormat="roundedDollar"
              />
            </>}
          </>}
          <Divider direction="horizontal" kind="secondary" />
          {dueAtProperty > 0 &&
            <PaymentSchedule direction="vertical" gap={8}>
              <Group direction="horizontal" horizontalAlign="space-between">
                <Heading variant="heading6" colour="highlight-secondary">
                  {intl.formatMessage({
                    id: 'ZelPTw2JSC',
                    defaultMessage: 'Due on checkout',
                    description: 'Heading for amount due on checkout',
                  })}
                </Heading>
                <Heading variant="heading6" colour="highlight-secondary" align="end">
                  <FormatCurrency value={total - dueAtProperty} format="preciseDollar" />
                </Heading>
              </Group>
              <Group direction="horizontal" horizontalAlign="space-between" className="dense">
                <BodyText variant="medium" weight="normal">
                  {intl.formatMessage({
                    id: 'Mssu0F6/z6',
                    defaultMessage: 'Due at property on arrival',
                    description: 'Label for amount due at property on arrival',
                  })}
                </BodyText>
                <BodyText variant="medium" weight="normal" align="end">
                  <FormatCurrency value={dueAtProperty} format="preciseDollar" />
                </BodyText>
              </Group>
            </PaymentSchedule>}
          {extraGuestMessage && <PriceDetailsModalExtraDesc>
            <BodyText variant="large" weight="bold">
              {intl.formatMessage({
                id: '58dzI8IKCT',
                defaultMessage: 'Extra guest surcharges',
                description: 'Heading for extra guest surcharges section',
              })}
            </BodyText>
            <MarkdownRender content={extraGuestMessage} />
          </PriceDetailsModalExtraDesc>}
          {dueAtPropertyMessage && <PriceDetailsModalExtraDesc>
            <BodyText variant="large" weight="bold">
              {intl.formatMessage({
                id: 'DBF7bJEqB/',
                defaultMessage: 'Due at property',
                description: 'Heading for due at property section',
              })}
            </BodyText>
            <MarkdownRender content={dueAtPropertyMessage} />
          </PriceDetailsModalExtraDesc>}
        </Group>
      </ModalContent>
    </ModalBody>
    {!!cta && <ModalFooter>
      <div onClick={() => resolve(undefined)}>
        {cta}
      </div>
    </ModalFooter>}
  </ModalBase>
}

export default PriceDetailsModal
